import { useEffect, useMemo, useState } from "react";
import { Building2, MapPin, MessageCircleMore, Search } from "lucide-react";
import { useNavigate } from "react-router";
import { ApiError, apiFetch } from "@contractor-quoting/api-client";
import { Button, Card } from "@contractor-quoting/shared-ui";
import { CustomerWorkspaceShell } from "./CustomerWorkspaceShell.js";
import { Shell } from "./Shell.js";
import { readDraftPrompt } from "../project-intake.js";
import {
  startAnonymousStructuredProject,
  startCustomerStructuredProject,
} from "../start-project-chat.js";

interface ContractorChoice {
  slug: string;
  name: string;
  description: string;
  brandPrimary: string;
  serviceArea: string | null;
}

export function ContractorSelector({
  mode = "anonymous",
  contractorSlug,
}: {
  mode?: "anonymous" | "customer" | "contractor";
  contractorSlug?: string;
}) {
  const navigate = useNavigate();
  const [items, setItems] = useState<ContractorChoice[]>([]);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [busySlug, setBusySlug] = useState("");
  const [error, setError] = useState("");
  const [draftPrompt] = useState(readDraftPrompt);

  useEffect(() => {
    void apiFetch<{ items: ContractorChoice[] }>("/v1/public/contractors")
      .then((result) => setItems(result.items))
      .catch((reason: unknown) =>
        setError(reason instanceof Error ? reason.message : "Contractors are unavailable"),
      )
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    const value = query.trim().toLowerCase();
    return items.filter(
      (item) =>
        (!contractorSlug || item.slug === contractorSlug) &&
        (!value ||
          [item.name, item.description, item.serviceArea ?? ""]
            .join(" ")
            .toLowerCase()
            .includes(value)),
    );
  }, [contractorSlug, items, query]);

  const selectContractor = async (item: ContractorChoice) => {
    if (!draftPrompt || busySlug) return;
    setBusySlug(item.slug);
    setError("");
    try {
      if (mode === "customer") {
        const projectId = await startCustomerStructuredProject(item.slug, draftPrompt);
        void navigate(`/projects/${projectId}`);
      } else {
        const destination = await startAnonymousStructuredProject(item.slug, draftPrompt);
        void navigate(destination);
      }
    } catch (reason) {
      if (reason instanceof ApiError && reason.code === "service_address_required") {
        void navigate(`/profile?returnTo=${encodeURIComponent("/chat")}`);
        return;
      }
      setError(reason instanceof Error ? reason.message : "The project could not be started");
      setBusySlug("");
    }
  };

  const content = (
    <main
      className={`${mode === "customer" ? "h-full overflow-y-auto" : "min-h-[calc(100vh-73px)]"} bg-stone-50 px-4 py-10 sm:px-6`}
    >
      <div className="mx-auto max-w-5xl">
        <p className="text-sm font-bold uppercase tracking-widest text-emerald-800">New project</p>
        <h1 className="mt-2 text-4xl font-black text-emerald-950">Choose a contractor</h1>
        <p className="mt-3 max-w-2xl leading-7 text-stone-600">
          Your project guide will classify the work after you choose. No project or chat session is
          created before this selection.
        </p>
        {draftPrompt ? (
          <div className="mt-5 flex gap-3 rounded-2xl border border-emerald-200 bg-emerald-50 p-4 text-emerald-950">
            <MessageCircleMore className="mt-0.5 shrink-0" size={19} />
            <p>
              <span className="font-bold">Your first message is saved:</span> “{draftPrompt}”
            </p>
          </div>
        ) : (
          <Card className="mt-5 text-amber-800">
            Describe your project before choosing a contractor.
          </Card>
        )}
        <label className="relative mt-7 block">
          <span className="sr-only">Search contractors</span>
          <Search className="absolute left-4 top-3.5 text-stone-400" size={19} />
          <input
            className="min-h-12 w-full rounded-xl border border-stone-300 bg-white pl-11 pr-4 outline-none focus:border-emerald-700 focus:ring-4 focus:ring-emerald-100"
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search by contractor, service, or location"
            value={query}
          />
        </label>
        {loading ? <p className="mt-8 text-stone-500">Loading available contractors…</p> : null}
        {error ? <Card className="mt-8 text-red-800">{error}</Card> : null}
        {!loading && !error && filtered.length === 0 ? (
          <Card className="mt-8 text-center">No quote-ready contractors match this project.</Card>
        ) : null}
        <div className="mt-8 grid gap-4 md:grid-cols-2">
          {filtered.map((item) => (
            <Card className="flex flex-col" key={item.slug}>
              <div className="flex items-start gap-3">
                <span
                  className="grid h-12 w-12 shrink-0 place-items-center rounded-xl text-lg font-black text-white"
                  style={{ backgroundColor: item.brandPrimary }}
                >
                  {item.name.charAt(0).toUpperCase() || <Building2 />}
                </span>
                <div>
                  <h2 className="text-xl font-black">{item.name}</h2>
                  {item.serviceArea ? (
                    <p className="mt-1 flex items-center gap-1 text-sm text-stone-500">
                      <MapPin size={14} /> {item.serviceArea}
                    </p>
                  ) : null}
                </div>
              </div>
              <p className="mt-4 flex-1 text-stone-600">
                {item.description || "Project planning and transparent starting estimates."}
              </p>
              <Button
                className="mt-4"
                disabled={!draftPrompt || Boolean(busySlug)}
                onClick={() => void selectContractor(item)}
              >
                {busySlug === item.slug ? "Starting…" : `Select ${item.name}`}
              </Button>
            </Card>
          ))}
        </div>
      </div>
    </main>
  );
  return mode === "customer" ? (
    <CustomerWorkspaceShell>{content}</CustomerWorkspaceShell>
  ) : (
    <Shell accountMode={mode === "contractor" ? "contractor" : "public"}>{content}</Shell>
  );
}
